<div class="space-y-6">
  <div class="flex items-center justify-between">
    <h1 class="text-2xl font-bold text-gray-900">Appointments</h1>
    <app-button variant="primary" (clicked)="openAddModal()">
      <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      Schedule Appointment
    </app-button>
  </div>

  <div class="card overflow-hidden">
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doctor</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
        @if (sortedAppointments().length === 0) {
            <tr>
              <td colspan="7" class="px-6 py-8 text-center text-gray-500">No appointments found</td>
            </tr>
          } @else {
            @for (appointment of sortedAppointments(); track appointment.id) {
              <tr class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ appointment.patientName }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ appointment.doctorName }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ appointment.date | date:'shortDate' }} at {{ appointment.time }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ appointment.type }}</td>
                <td class="px-6 py-4 text-sm text-gray-900">{{ appointment.reason }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <select [value]="appointment.status" 
                          (change)="updateStatus(appointment.id, $any($event.target).value)"
                          class="text-xs px-2 py-1 rounded-full border-0"
                          [ngClass]="{
                            'bg-blue-100 text-blue-800': appointment.status === 'Scheduled',
                            'bg-green-100 text-green-800': appointment.status === 'Confirmed',
                            'bg-yellow-100 text-yellow-800': appointment.status === 'In Progress',
                            'bg-gray-100 text-gray-800': appointment.status === 'Completed',
                            'bg-red-100 text-red-800': appointment.status === 'Cancelled'
                          }">
                    <option value="Scheduled">Scheduled</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <a [routerLink]="['/consultation']" [queryParams]="{patientId: appointment.patientId}" 
                     class="text-green-600 hover:text-green-900 mr-4 font-medium">Start Consultation</a>
                  <button (click)="openEditModal(appointment)" class="text-primary-600 hover:text-primary-900 mr-4">Edit</button>
                  <app-button variant="danger" (clicked)="deleteAppointment(appointment.id)">Delete</app-button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  </div>

  @if (showModal()) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" (click)="closeModal()">
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" (click)="$event.stopPropagation()">
        <div class="p-6 border-b">
          <h2 class="text-xl font-semibold text-gray-900">
            {{ editingAppointment() ? 'Edit Appointment' : 'Schedule Appointment' }}
          </h2>
        </div>
        <div class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Patient</label>
            <select [(ngModel)]="appointmentForm().patientId" (change)="onPatientSelect($any($event.target).value)" class="input-field">
              <option value="">Select Patient</option>
              @for (patient of patients(); track patient.id) {
                <option [value]="patient.id">{{ patient.firstName }} {{ patient.lastName }}</option>
              }
            </select>
          </div>
          <div>
            <app-input
              label="Doctor"
              type="text"
              [(ngModel)]="appointmentForm().doctorName">
            </app-input>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-date
                label="Date"
                [(ngModel)]="appointmentForm().date"
                [required]="true"
                name="appointmentDate"
                (ngModelChange)="onDateChange($event)">
              </app-date>
            </div>
            <div>
              <app-input
                label="Time"
                type="time"
                [(ngModel)]="appointmentForm().time">
              </app-input>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select [(ngModel)]="appointmentForm().type" class="input-field">
                <option value="Consultation">Consultation</option>
                <option value="Follow-up">Follow-up</option>
                <option value="Checkup">Checkup</option>
                <option value="Emergency">Emergency</option>
                <option value="Surgery">Surgery</option>
              </select>
            </div>
            <div>
              <app-input
                label="Duration (minutes)"
                type="number"
                [(ngModel)]="appointmentForm().duration">
              </app-input>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea [(ngModel)]="appointmentForm().reason" class="input-field" rows="3"></textarea>
          </div>
        </div>
        <div class="p-6 border-t flex justify-end space-x-3">
          <app-button variant="secondary" (clicked)="closeModal()">Cancel</app-button>
          <app-button variant="primary" (clicked)="saveAppointment()">Save</app-button>
        </div>
      </div>
    </div>
  }
</div>

